import React, { useState, useRef, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { malletSVG, fmt, esc } from "../lib/mallet";

export default function CartDrawer() {
  const { cart, changeQty, clearCart, cartTotal, shipping, drawerOpen, closeCart, toast } = useCart();
  const [view, setView] = useState("cart"); // cart | checkout | done
  const [done, setDone] = useState({ orderNo: "", msg: "" });
  const formRef = useRef(null);

  useEffect(() => {
    if (drawerOpen) setView("cart");
  }, [drawerOpen]);

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") closeCart(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeCart]);

  const placeOrder = () => {
    const f = formRef.current;
    if (!f.reportValidity()) return;
    const d = Object.fromEntries(new FormData(f).entries());
    const lines = cart.map(c => `${c.qty} x ${c.name} (${c.detail}) ${fmt(c.price * c.qty)}`).join("\n");
    const msg = `New order — Aubone Polo Mallets\n\n${lines}\nShipping: ${shipping ? fmt(shipping) : "free"}\nTotal: ${fmt(cartTotal + shipping)}\n\nName: ${d.name}\nEmail: ${d.email}\nPhone: ${d.phone}\nShip to: ${d.address}, ${d.city} ${d.zip}\nNotes: ${d.notes || "-"}`;
    const orderNo = "AB-" + Date.now().toString().slice(-6);
    setDone({ orderNo, msg });
    setView("done");
  };

  const copyOrder = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(done.msg).then(() => toast("Order copied")).catch(() => toast("Copy failed — use text or email"));
    } else {
      toast("Copy failed — use text or email");
    }
  };

  return (
    <>
      <div className="drawer-bg" hidden={!drawerOpen} onClick={closeCart} />
      <aside className={"drawer" + (drawerOpen ? " open" : "")} aria-label="Shopping cart" aria-hidden={!drawerOpen}>
        <div className="drawer-head">
          <h3>Your cart</h3>
          <button className="btn btn-ghost" style={{ color: "var(--ink)", borderColor: "rgba(28,26,24,.25)" }} onClick={closeCart}>Close</button>
        </div>

        {view === "cart" && (
          <>
            <div className="drawer-body">
              {cart.length === 0 ? (
                <div className="empty">Your cart is empty. Pick a mallet from the rack or design one.</div>
              ) : cart.map((c, i) => (
                <div className="cart-item" key={c.key}>
                  <div className="art" dangerouslySetInnerHTML={{ __html: malletSVG(c.spec, { vertical: true }) }} />
                  <div><strong>{c.name}</strong><small>{c.detail}</small><small>{fmt(c.price)} each</small></div>
                  <div className="qty">
                    <button aria-label="Fewer" onClick={() => changeQty(i, -1)}>−</button>
                    <span>{c.qty}</span>
                    <button aria-label="More" onClick={() => changeQty(i, 1)}>+</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="drawer-foot">
              {cart.length > 0 && (
                <>
                  <div className="tot"><span>Subtotal</span><span>{fmt(cartTotal)}</span></div>
                  <div className="tot" style={{ fontWeight: 400, color: "#6B6255" }}><span>Shipping</span><span>{shipping ? fmt(shipping) : "Free"}</span></div>
                  <div className="tot"><span>Total</span><span>{fmt(cartTotal + shipping)}</span></div>
                  <button className="btn btn-primary" style={{ justifyContent: "center" }} onClick={() => setView("checkout")}>Check out</button>
                </>
              )}
            </div>
          </>
        )}

        {view === "checkout" && (
          <>
            <div className="drawer-body">
              <form className="checkout" ref={formRef} onSubmit={e => e.preventDefault()}>
                <h3>Where should we send it?</h3>
                <input name="name" placeholder="Full name" required />
                <div className="two"><input name="email" type="email" placeholder="Email" required /><input name="phone" type="tel" placeholder="Phone" required /></div>
                <input name="address" placeholder="Street address" required />
                <div className="two"><input name="city" placeholder="City" required /><input name="zip" placeholder="ZIP" required /></div>
                <textarea name="notes" rows="3" placeholder="Pony height, club colors, artwork, anything we should know" />
                <div className="summary">{cart.map(c => `${c.qty} × ${c.name} — ${c.detail} — ${fmt(c.price * c.qty)}`).join("\n")}{"\n"}Shipping {shipping ? fmt(shipping) : "free"}{"\n"}Total {fmt(cartTotal + shipping)}</div>
                <p style={{ fontSize: ".9rem", color: "#6B6255" }}>Card payment is collected by phone or secure link once we confirm your order — we call every custom order before we cut cane.</p>
              </form>
            </div>
            <div className="drawer-foot">
              <button className="btn btn-primary" style={{ justifyContent: "center" }} onClick={placeOrder}>Send order</button>
              <button className="btn btn-ghost" style={{ justifyContent: "center", color: "var(--ink)", borderColor: "rgba(28,26,24,.25)" }} onClick={() => setView("cart")}>Back to cart</button>
            </div>
          </>
        )}

        {view === "done" && (
          <>
            <div className="drawer-body">
              <div className="empty" style={{ color: "var(--ink)" }}>
                <h3>Order {done.orderNo} ready to send</h3>
                <p style={{ margin: ".75rem auto 1.25rem", color: "#6B6255" }}>Send it to the shop by text or email and we'll confirm within one business day.</p>
                <div style={{ display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  <a className="btn btn-primary" style={{ justifyContent: "center" }} href={`sms:+16305367971?&body=${encodeURIComponent(done.msg)}`}>Text the order</a>
                  <a className="btn btn-paper" style={{ justifyContent: "center" }} href={`mailto:orders@aubonepolomallets.com?subject=${encodeURIComponent("Order " + done.orderNo)}&body=${encodeURIComponent(done.msg)}`}>Email the order</a>
                  <button className="btn btn-ghost" style={{ justifyContent: "center", color: "var(--ink)", borderColor: "rgba(28,26,24,.25)" }} onClick={copyOrder}>Copy order details</button>
                </div>
              </div>
            </div>
            <div className="drawer-foot">
              <button className="btn btn-ghost" style={{ justifyContent: "center", color: "var(--ink)", borderColor: "rgba(28,26,24,.25)" }} onClick={() => { clearCart(); setView("cart"); }}>Start a new cart</button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
